import { zodResolver } from "@hookform/resolvers/zod";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { useKudosCelebration } from "@/features/kudos/celebration/celebration.context";
import {
  formatRecipientNames,
  isSelfKudosError,
  LAST_VALUE_TAGS,
  SUGGESTED_VALUE_TAGS,
  toKudosRecipientOptions,
} from "@/features/kudos/components/create-kudos-dialog.helpers";
import { SuggestedValueChips } from "@/features/kudos/components/suggested-value-chips";
import { useCreateKudos } from "@/features/kudos/hooks/use-create-kudos";
import { useKudosWritingAssist } from "@/features/kudos/hooks/use-kudos-writing-assist";
import {
  type CreateKudosInput,
  createKudosInputSchema,
} from "@/features/kudos/schemas/kudos.schema";
import { useAuth } from "@/shared/auth/auth.context";
import { ChipsField } from "@/shared/components/form/fields/chips-field";
import { MultiUserField } from "@/shared/components/form/fields/multi-user-field";
import { TextareaField } from "@/shared/components/form/fields/textarea-field";
import { FormDialog } from "@/shared/components/form/form-dialog";
import type { MultiUserPickerOption } from "@/shared/components/multi-user-picker/multi-user-picker-list";
import { ApiError } from "@/shared/lib/api-call";
import { useFormPref } from "@/shared/lib/form-prefs";
import { usePeopleList } from "@/shared/membership/use-people-list";
import { WritingAssistLabelAction } from "@/shared/writing-assistant/writing-assist-label-action";
import { WritingAssistRevertLink } from "@/shared/writing-assistant/writing-assist-revert-link";
import { WritingAssistShimmer } from "@/shared/writing-assistant/writing-assist-shimmer";

export interface PresetKudosRecipient {
  userId: string;
  displayName: string;
  email?: string | null;
  avatarUrl?: string | null;
}

interface Props {
  workspaceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  /// Relationship-board entry point: prefill the recipient with this person.
  presetRecipient?: PresetKudosRecipient | null;
}

const MESSAGE_MAX = 2500;
const MESSAGE_MIN = 25;
const MAX_RECIPIENTS = 10;
const MAX_VALUE_TAGS = 10;

/// New-kudos dialog. RHF + Zod via the shared `<FormDialog>` (F1, F2).
/// Persists the user's last `valueTags` selection as a smart default (F6).
/// On 400 SELF_KUDOS_NOT_ALLOWED: inline recipient error; on success:
/// toast + close + invalidate.
export function CreateKudosDialog({
  workspaceId,
  open,
  onOpenChange,
  onSuccess,
  presetRecipient,
}: Props): ReactNode {
  const auth = useAuth();
  const mut = useCreateKudos(workspaceId);
  // Gate on `open`: this dialog is mounted app-wide (via the onboarding hub),
  // so an ungated fetch pulled the roster on every page. Only fetch when open.
  const peopleQuery = usePeopleList(open ? workspaceId : undefined, {
    status: "all",
    limit: 100,
  });
  const [lastValueTags, persistLastValueTags] = useFormPref(LAST_VALUE_TAGS);
  const [serverError, setServerError] = useState<string | null>(null);
  const celebration = useKudosCelebration();

  const form = useForm<CreateKudosInput>({
    resolver: zodResolver(createKudosInputSchema),
    defaultValues: {
      recipientUserIds: [],
      message: "",
      valueTags: [...lastValueTags],
    },
  });

  // Only people who have joined. The helper also excludes self.
  const recipientOptions = useMemo<MultiUserPickerOption[]>(
    () => toKudosRecipientOptions(peopleQuery.data?.rows ?? [], auth.user?.id),
    [peopleQuery.data?.rows, auth.user?.id],
  );

  // Resolver merges loaded options with the preset descriptor so chips
  // render on first paint, before the people-list query resolves.
  const recipientById = useMemo(() => {
    const map = new Map<string, MultiUserPickerOption>();
    for (const o of recipientOptions) map.set(o.userId, o);
    if (presetRecipient && !map.has(presetRecipient.userId)) {
      map.set(presetRecipient.userId, {
        userId: presetRecipient.userId,
        displayName: presetRecipient.displayName,
        email: presetRecipient.email ?? "",
        avatarUrl: presetRecipient.avatarUrl ?? null,
      });
    }
    return map;
  }, [recipientOptions, presetRecipient]);

  const message = form.watch("message") ?? "";
  const messageLen = message.length;
  const valueTags = (form.watch("valueTags") ?? []) as ReadonlyArray<string>;
  const { assist, ...gate } = useKudosWritingAssist(form, workspaceId, recipientById);

  useEffect(() => {
    if (open) {
      form.reset({
        recipientUserIds: presetRecipient?.userId ? [presetRecipient.userId] : [],
        message: "",
        valueTags: [...lastValueTags],
      });
      setServerError(null);
      // Drop any stale draft snapshot so revert can't reach across opens.
      assist.reset();
    }
    // reason: re-run on open + preset; lastValueTags is read once per open
  }, [open, presetRecipient?.userId, form, lastValueTags, assist.reset]);

  function appendSuggestedTag(tag: string) {
    if (valueTags.includes(tag)) return;
    if (valueTags.length >= MAX_VALUE_TAGS) return;
    form.setValue("valueTags", [...valueTags, tag], {
      shouldDirty: true,
      shouldValidate: true,
    });
  }

  async function onSubmit(input: CreateKudosInput) {
    setServerError(null);
    const tags = (input.valueTags ?? []).map((t) => t.trim()).filter((t) => t.length > 0);
    const payload: CreateKudosInput = {
      recipientUserIds: input.recipientUserIds,
      message: input.message.trim(),
      ...(tags.length > 0 ? { valueTags: tags } : {}),
    };
    try {
      await mut.mutateAsync(payload);
      persistLastValueTags(tags);
      const names = input.recipientUserIds
        .map((id) => recipientById.get(id)?.displayName)
        .filter((n): n is string => Boolean(n));
      toast.success(`Kudos sent to ${formatRecipientNames(names)}`);
      onOpenChange(false);
      celebration?.celebrate("give");
      onSuccess?.();
    } catch (err) {
      if (err instanceof ApiError && isSelfKudosError(err)) {
        form.setError("recipientUserIds", {
          type: "server",
          message: "You can't send kudos to yourself.",
        });
        return;
      }
      setServerError(err instanceof Error ? err.message : "Couldn't send kudos.");
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      form={form}
      onSubmit={onSubmit}
      title="Give kudos"
      description="Celebrate up to 10 teammates with a specific, public thank-you."
      submitLabel="Send kudos"
      submitLoadingLabel="Sending…"
      serverError={serverError}
      submitDisabled={assist.isGenerating}
      size="2xl"
    >
      <MultiUserField
        control={form.control}
        name="recipientUserIds"
        label="Recipients"
        options={recipientOptions}
        max={MAX_RECIPIENTS}
        autoFocusKey={open ? "open" : "closed"}
        searchPlaceholder="Search teammates by name or email…"
        emptyState={
          <p className="px-3 py-6 text-center text-xs text-text-tertiary">
            {peopleQuery.isLoading ? "Loading…" : "No teammates found."}
          </p>
        }
      />

      <TextareaField
        control={form.control}
        name="message"
        label="Message"
        placeholder="What did they do, and why does it matter?"
        rows={5}
        maxLength={MESSAGE_MAX}
        disabled={assist.isGenerating}
        hint={
          messageLen < MESSAGE_MIN
            ? `Minimum ${MESSAGE_MIN} characters, ${MESSAGE_MIN - messageLen} to go.`
            : `Minimum ${MESSAGE_MIN} characters.`
        }
        labelAction={
          <WritingAssistLabelAction
            canAssist={gate.canAssist}
            value={message}
            assist={assist}
            disabledReason={gate.disabledReason}
          />
        }
        overlay={assist.isGenerating ? <WritingAssistShimmer /> : null}
      />
      {assist.canRevert ? <WritingAssistRevertLink onClick={assist.revert} /> : null}

      <SuggestedValueChips
        suggestions={SUGGESTED_VALUE_TAGS}
        selected={valueTags}
        max={MAX_VALUE_TAGS}
        onAppend={appendSuggestedTag}
      />

      <ChipsField
        control={form.control}
        name="valueTags"
        label="Value tags (optional)"
        max={MAX_VALUE_TAGS}
        maxLength={50}
        placeholder="Press Enter to add (e.g. teamwork, ownership)"
      />
    </FormDialog>
  );
}
